import { Component, type ErrorInfo } from "react";

interface ErrorBoundaryProps {
	readonly children: React.ReactNode;
}

interface ErrorBoundaryState {
	readonly hasError: boolean;
	readonly error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { hasError: true, error };
	}

	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	shouldComponentUpdate(nextProps: ErrorBoundaryProps, nextState: ErrorBoundaryState) {
		return nextState.hasError !== this.state.hasError || nextState.error !== this.state.error;
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		console.error("ErrorBoundary caught an error:", error, errorInfo);
	}

	render() {
		if (this.state.hasError) {
			return (
				<div className="p-4 bg-red-100 text-red-800 rounded">
					<h2 className="text-lg font-semibold">Something went wrong.</h2>
					<p>{this.state.error?.message}</p>
				</div>
			);
		}

		return this.props.children;
	}
}